(function () {
  /*
   * Sales Agent entry points — shared consent gate and handoff.
   *
   * Four entry points (nav level 1 + 2, search, PDP How-To) hand off to the same external
   * widget:
   *   window.magnamateWidget.openSalesAgent({ message, product })  — submits `message`
   *   window.magnamateWidget.open()                                — opens the bubble only
   *
   * Nav and search entry points sit inside an overlay that renders above the widget, so
   * their host is closed FIRST and the agent opens on the next frame.
   *
   * Consent (Option B, per MNR-717): entry points ship [hidden] and are only revealed once
   * Usercentrics reports consent, because the widget cannot load without it.
   */
  if (window.salesAgentEntryInit) return;
  window.salesAgentEntryInit = true;

  var ENTRY_SELECTOR = '[data-sales-agent-entry]';

  function getEntries() {
    return Array.prototype.slice.call(document.querySelectorAll(ENTRY_SELECTOR));
  }

  /* --------------------------------------------------------------------- *
   * Consent (Usercentrics), mirroring assets/inline-sales-agent.js
   * --------------------------------------------------------------------- */

  // Service name comes from shop.metafields.mnr.sales_agent_uc_service_name.
  function getServiceName(entry) {
    var raw = (entry.getAttribute('data-uc-service') || '').trim();
    return (raw || 'sales agent').toLowerCase();
  }

  async function getServices() {
    if (typeof UC_UI === 'undefined' || !UC_UI.getServicesBaseInfo) return [];
    try {
      return (await UC_UI.getServicesBaseInfo()) || [];
    } catch (e) {
      console.warn('Sales Agent entry: UC_UI.getServicesBaseInfo() failed:', e);
      return [];
    }
  }

  function findService(services, serviceName) {
    for (var i = 0; i < services.length; i++) {
      if (((services[i] || {}).name || '').toLowerCase().includes(serviceName)) return services[i];
    }
    return null;
  }

  function hasConsent(service) {
    return !!(service && service.consent && service.consent.status === true);
  }

  function reveal(entry) {
    // Cached so the click handler can re-check consent synchronously.
    entry.dataset.consentGranted = '1';
    entry.removeAttribute('hidden');
  }

  function hide(entry) {
    delete entry.dataset.consentGranted;
    // Do not strand focus on an element we are about to hide.
    if (entry.contains(document.activeElement) && document.activeElement.blur) {
      document.activeElement.blur();
    }
    entry.setAttribute('hidden', '');
  }

  // Last known decision per service, so entry points inserted later can be re-gated
  // synchronously — an async pass would leave them visible-but-dead for a frame.
  var consentByService = {};

  function applyConsentSync() {
    getEntries().forEach(function (entry) {
      // Unknown service (no pass has run yet) stays hidden — fail closed.
      if (consentByService[getServiceName(entry)]) reveal(entry);
      else hide(entry);
    });
  }

  async function applyConsent() {
    var services = await getServices();
    // Query AFTER the await, or entries inserted meanwhile are missed.
    getEntries().forEach(function (entry) {
      var name = getServiceName(entry);
      var granted = hasConsent(findService(services, name));
      consentByService[name] = granted;
      if (granted) reveal(entry);
      else hide(entry);
    });
  }

  /* --------------------------------------------------------------------- *
   * Closing the host overlay
   * --------------------------------------------------------------------- */

  // Mobile nav drawer.
  //
  // Two things about global.js make this longer than it looks. closeMenuDrawer() needs a
  // truthy event AND element or it early-returns. And it does not finish the job:
  // closeAnimation() defers `removeAttribute('open')` by 400ms, during which the drawer
  // still covers the agent — plus `.section-header.menu-open` and the body scroll lock
  // outlive it entirely. So we run the theme's close, then force the end state (idempotent:
  // the pending callback just removes an attribute that is already gone).
  function closeMenuDrawer() {
    var drawer = document.querySelector('header-drawer');
    if (!drawer || typeof drawer.closeMenuDrawer !== 'function') return false;
    var summary = drawer.querySelector('summary');
    if (!summary) return false;

    try {
      drawer.closeMenuDrawer(new Event('click'), summary);
      // closeMenuDrawer() ends in removeTrapFocus(summary), which focuses the hamburger we
      // just closed. We are handing off to the widget, so drop it.
      if (document.activeElement === summary && summary.blur) summary.blur();
    } catch (e) {
      console.warn('Sales Agent entry: closeMenuDrawer() threw, forcing the drawer shut:', e);
    }

    try {
      Array.prototype.forEach.call(drawer.querySelectorAll('details'), function (details) {
        details.classList.remove('menu-opening');
        details.removeAttribute('open');
      });
      Array.prototype.forEach.call(drawer.querySelectorAll('.submenu-open'), function (submenu) {
        submenu.classList.remove('submenu-open');
      });
      Array.prototype.forEach.call(drawer.querySelectorAll('summary[aria-expanded="true"]'), function (el) {
        el.setAttribute('aria-expanded', 'false');
      });

      var header = document.querySelector('.section-header');
      if (header) header.classList.remove('menu-open');
      var breakpoint = drawer.dataset ? drawer.dataset.breakpoint : null;
      if (breakpoint) document.body.classList.remove('overflow-hidden-' + breakpoint);
      document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-desktop');
    } catch (e) {
      console.warn('Sales Agent entry: forcing the drawer shut failed:', e);
    }

    return true;
  }

  // Search overlay. The header renders up to two instances; close whichever is open.
  // close(false), not close(): the default refocuses the search toggle, which on mobile
  // re-opens the keyboard over the agent.
  function closeSearchOverlay() {
    var modals = document.querySelectorAll('details-modal.header__search');
    var wasOpen = false;
    Array.prototype.forEach.call(modals, function (modal) {
      var open = typeof modal.isOpen === 'function' ? modal.isOpen() : false;
      if (!open) return;
      wasOpen = true;
      try {
        if (typeof modal.close === 'function') modal.close(false);
      } catch (e) {
        console.warn('Sales Agent entry: close() threw, forcing the overlay shut:', e);
      }
      // Force the end state even if close() threw — otherwise we would report "not closed"
      // and open the agent behind a still-open overlay.
      var details = modal.querySelector('details');
      if (details) details.removeAttribute('open');
      // Scoped to the branch that actually closed a modal: the scroll lock belongs to
      // DetailsModal, so we only undo it where we took over its close.
      document.body.classList.remove('overflow-hidden');
    });

    return wasOpen;
  }

  function closeHostOverlay(entry) {
    var host = entry.getAttribute('data-sales-agent-entry-host');
    if (host === 'drawer') return closeMenuDrawer();
    if (host === 'search') return closeSearchOverlay();
    return false;
  }

  /* --------------------------------------------------------------------- *
   * Handoff
   * --------------------------------------------------------------------- */

  // Read at click time — a flavour change patches only a fixed list of ids, not this markup,
  // so a server-rendered name would go stale. Scoped to the PDP: quick-add has its own datalayer.
  function currentVariantTitle() {
    var scope = document.querySelector('product-info[id^="MainProduct-"]') || document;

    var node = scope.querySelector('variant-selects [data-selected-variant]');
    if (node) {
      try {
        var variant = JSON.parse(node.textContent);
        if (variant && variant.title) return variant.title;
      } catch (e) {
        /* Fall through to the datalayer. */
      }
    }

    var tracking = scope.querySelector('.product-item-datalayer');
    if (tracking) {
      try {
        var data = JSON.parse(tracking.textContent);
        if (data && data.item_variant) return data.item_variant;
      } catch (e) {
        /* No usable variant name. */
      }
    }

    return null;
  }

  // "<title>: <variant>: <question>". Only for the PDP question block — the older How-To
  // labels already contain the title. Missing segments are dropped, not left empty.
  function composeMessage(entry, trigger) {
    var question = (trigger.getAttribute('data-sales-agent-entry-question') || '').trim();
    if (!question) return null;
    if (!entry.hasAttribute('data-sales-agent-entry-pdp-questions')) return question;

    var segments = [];

    var title = (entry.getAttribute('data-product-title') || '').trim();
    if (title) segments.push(title);

    // "Default Title" is Shopify's name for a single-variant product's only variant.
    var variantTitle = (currentVariantTitle() || '').trim();
    if (variantTitle && variantTitle !== 'Default Title' && variantTitle !== title) {
      segments.push(variantTitle);
    }

    segments.push(question);

    return segments.join(': ');
  }

  // Only the PDP entry point carries product context.
  function productContext(entry) {
    var handle = entry.getAttribute('data-product-handle');
    var title = entry.getAttribute('data-product-title');
    if (!handle && !title) return undefined;
    var image = entry.getAttribute('data-product-image');
    var product = { handle: handle || '', title: title || '' };
    if (image) product.imageUrl = image;
    return product;
  }

  function openAgent(entry, message) {
    // Re-checked here because the cached flag goes stale if consent is revoked after reveal.
    if (entry.dataset.consentGranted !== '1') return false;

    var widget = window.magnamateWidget;
    if (!widget) {
      console.error('Sales Agent entry: window.magnamateWidget is not available.');
      return false;
    }

    try {
      // A message means openSalesAgent(), which submits it. Without one we only open the
      // bubble, so no question is sent.
      if (message) {
        if (typeof widget.openSalesAgent !== 'function') {
          console.error('Sales Agent entry: magnamateWidget.openSalesAgent() is not available.');
          return false;
        }
        var product = productContext(entry);
        widget.openSalesAgent(product ? { message: message, product: product } : { message: message });
        return true;
      }

      if (typeof widget.open === 'function') {
        widget.open();
        return true;
      }
      // Fallback for builds exposing only openSalesAgent.
      if (typeof widget.openSalesAgent === 'function') {
        widget.openSalesAgent({});
        return true;
      }
      console.error('Sales Agent entry: no usable open method on magnamateWidget.');
      return false;
    } catch (e) {
      console.error('Sales Agent entry: handoff failed:', e);
      return false;
    }
  }

  function handoff(entry, message) {
    if (!closeHostOverlay(entry)) {
      openAgent(entry, message);
      return;
    }
    // The close above is synchronous, so one frame is enough to paint it. Blur first:
    // focus is still inside the collapsed markup, and on mobile that keeps the keyboard up.
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    requestAnimationFrame(function () {
      openAgent(entry, message);
    });
  }

  /* --------------------------------------------------------------------- *
   * Click handling — one delegated listener, capture phase so it survives section
   * re-renders and fires before MenuDrawer's own per-<button> close handlers.
   * --------------------------------------------------------------------- */
  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest && event.target.closest('[data-sales-agent-entry-trigger]');
      if (!trigger) return;

      var entry = trigger.closest(ENTRY_SELECTOR);
      if (!entry) return;

      // Never let the nav/search host treat this as a normal link activation.
      //
      // preventDefault() only — NOT stopPropagation(). Kameleoon's engine.js registers its
      // click goals by document-level delegation, and stopping propagation in the capture
      // phase here would kill every one of them: the whole point of this test is counting
      // these clicks. Nothing in the theme needs guarding against anyway —
      // MenuDrawer.bindEvents() binds only <summary> and <button> (assets/global.js:1340),
      // which is why nav1/nav2/search are <a role="button">, and DetailsModal.onBodyClick
      // ignores clicks inside the modal (assets/details-modal.js:23).
      event.preventDefault();

      // Only the question pills carry text to submit; everything else just opens the bubble.
      handoff(entry, composeMessage(entry, trigger));
    },
    true
  );

  // The <a role="button"> entry points do not get Space/Enter for free like a <button>.
  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      var target = event.target;
      if (!target || !target.closest) return;
      var trigger = target.closest('a[role="button"][data-sales-agent-entry-trigger]');
      if (!trigger) return;
      event.preventDefault();
      trigger.click();
    },
    true
  );

  /* --------------------------------------------------------------------- *
   * Boot
   * --------------------------------------------------------------------- */

  // A zero-result search re-renders sections/predictive-search.liquid into
  // [data-predictive-search] via innerHTML (assets/predictive-search.js:245). That section
  // renders predictive-search-default a SECOND time (sections/predictive-search.liquid:16),
  // so a fresh entry point appears carrying its server-rendered `hidden` — without firing
  // UC_CONSENT or shopify:section:load. Without this it stays hidden for the rest of the
  // session. All instances are observed: the header renders up to two (sections/header.liquid
  // lines 191 and 332), same as closeSearchOverlay().
  function watchForReinsertedEntries() {
    if (typeof MutationObserver === 'undefined') return;
    Array.prototype.forEach.call(document.querySelectorAll('predictive-search'), function (host) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        // Coalesce: one innerHTML swap fires many records.
        requestAnimationFrame(function () {
          pending = false;
          applyConsentSync();
        });
      }).observe(host, { childList: true, subtree: true });
    });
  }

  function init() {
    applyConsent();
    window.addEventListener('UC_CONSENT', function () {
      applyConsent();
    });
    document.addEventListener('shopify:section:load', function () {
      applyConsent();
    });
    // A cross-product flavour switch replaces the whole <product-info> node
    // (product-info.js#handleSwapProduct), so the PDP entries come back with their
    // server-rendered `hidden` and neither event above fires. The replacement dispatches
    // this one from connectedCallback.
    document.addEventListener('product-info:loaded', function () {
      applyConsentSync();
    });
    watchForReinsertedEntries();
  }

  // Must wait for UC to initialise. This script is deferred but still runs before UC_UI
  // exists, and UC_CONSENT only fires when consent CHANGES — so a returning visitor with
  // stored consent would never be re-checked. Same guard as inline-sales-agent.js.
  if (typeof UC_UI !== 'undefined' && UC_UI.isInitialized && UC_UI.isInitialized()) {
    init();
  } else {
    window.addEventListener('UC_UI_INITIALIZED', init);
  }
})();
